import { useState } from "react";
import axios from "axios";

import type {
    CreateUploadUrlDto,
    CreateDocumentDto,
    UserDtoDocumentsInnerTypeEnum,
} from "../../../api/api";
import { getDocumentDisplayName } from "../../../utils/onboarding";
import { documentsService } from "../../api_client";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";

interface UseDocumentUploadProps {
    onUploadSuccess?: () => void;
}

export function useDocumentUpload({ onUploadSuccess }: UseDocumentUploadProps = {}) {
    const [ uploading, setUploading ] = useState<
        Record<UserDtoDocumentsInnerTypeEnum, boolean>
    >({
        CR: false,
        TAX_CARD: false,
    });

    const [ selectedFiles, setSelectedFiles ] = useState<
        Record<UserDtoDocumentsInnerTypeEnum, File | null>
    >({
        CR: null,
        TAX_CARD: null,
    });

    const [ uploadProgress, setUploadProgress ] = useState<
        Record<UserDtoDocumentsInnerTypeEnum, number>
    >({
        CR: 0,
        TAX_CARD: 0,
    });

    const handleFileSelect = (
        type: UserDtoDocumentsInnerTypeEnum,
        file: File | null
    ) => {
        if (!file) {
            setSelectedFiles((prev) => ({ ...prev, [ type ]: null }));
            return;
        }

        setSelectedFiles((prev) => ({ ...prev, [ type ]: file }));
    };

    const handleFileRemove = (type: UserDtoDocumentsInnerTypeEnum) => {
        setSelectedFiles((prev) => ({ ...prev, [ type ]: null }));
    };

    const uploadDocument = async (
        type: UserDtoDocumentsInnerTypeEnum,
        file: File
    ) => {
        setUploading((prev) => ({ ...prev, [ type ]: true }));
        setUploadProgress((prev) => ({ ...prev, [ type ]: 0 }));

        try {
            // Step 1: Get upload URL
            const uploadUrlRequest: CreateUploadUrlDto = {
                type,
                contentTypes: [ file.type ],
            };

            const uploadUrlResponse = await documentsService.createDocumentUploadUrl(
                uploadUrlRequest
            );
            const uploadUrlData = uploadUrlResponse.data[ 0 ];

            if (!uploadUrlData) {
                throw new Error("Failed to get upload URL");
            }

            // Step 2: Upload file to storage with real progress
            // Do NOT set Content-Length - it's a forbidden header; the browser sets it automatically.
            const headers: Record<string, string> = {
                ...(uploadUrlData.headers as Record<string, string>),
            };

            if (!headers[ "Content-Type" ]) {
                headers[ "Content-Type" ] = file.type;
            }

            await axios.put(uploadUrlData.uploadUrl, file, {
                headers,
                onUploadProgress: (event) => {
                    if (!event.total) return;
                    const progress = event.loaded / event.total; // 0–1
                    setUploadProgress((prev) => ({
                        ...prev,
                        [ type ]: progress,
                    }));
                },
            });

            // Ensure final state is 100%
            setUploadProgress((prev) => ({ ...prev, [ type ]: 1 }));

            // Step 3: Create document record
            const createDocumentRequest: CreateDocumentDto = {
                type,
                objectKey: uploadUrlData.objectKey,
                fileName: file.name,
                contentType: file.type,
                fileSize: file.size,
            };

            await documentsService.createDocument(createDocumentRequest);

            showSuccessToast(`${getDocumentDisplayName(type)} uploaded successfully!`);

            // Clear selected file after successful upload
            setSelectedFiles((prev) => ({ ...prev, [ type ]: null }));

            // Call success callback
            onUploadSuccess?.();
        } catch (error) {
            console.error("Upload error:", error);
            showErrorToast(
                `Failed to upload ${getDocumentDisplayName(type)}. Please try again.`
            );
        } finally {
            setUploading((prev) => ({ ...prev, [ type ]: false }));
        }
    };

    const handleConfirmUpload = async (
        type: UserDtoDocumentsInnerTypeEnum
    ) => {
        const file = selectedFiles[ type ];
        if (!file) return;
        await uploadDocument(type, file);
    };

    return {
        uploading,
        uploadProgress,
        selectedFiles,
        handleFileSelect,
        handleFileRemove,
        handleConfirmUpload,
        uploadDocument,
    };
}
